<template>
	<div v-if="visible" class="account-center-overlay" @click.self="handleClose">
		<div class="account-center-container">
			<!-- 关闭按钮 -->
			<div class="close-btn" @click="handleClose">
				<i class="el-icon-close"></i>
			</div>
			
			<div class="account-center-content">
				<!-- 左侧菜单 -->
				<div class="account-sidebar">
					<div 
						v-for="(menu, index) in menuItems" 
						:key="index"
						:class="['menu-item', { 'menu-active': activeTab === menu.tab }, { 'menu-danger': menu.tab === 'logout' }]"
						@click="handleMenuClick(menu.tab)"
					>
						{{ menu.label }}
					</div>
				</div>
				
				<!-- 右侧内容区域 -->
				<div class="account-main-content">
				<!-- 钱包内容 -->
				<div v-show="activeTab === 'wallet'" class="content-panel wallet-panel">
					<!-- 账户卡片网格 -->
					<div class="accounts-grid">
						<!-- 普通账户 -->
						<div class="account-card account-card-spot">
							<div class="card-header">
								<div class="account-icon">
									<i class="el-icon-wallet"></i>
								</div>
								<div class="account-name">{{ $t('transfer.spotAccount') || '普通账户' }}</div>
							</div>
							<div class="account-balance">{{ formatBalance(accountBalances.spotBalance) }}</div>
							<div class="account-details">
								<div class="detail-row">
									<span class="label">{{ $t('transfer.available') || '可用' }}</span>
									<span class="value">{{ formatBalance(accountBalances.spotAvailable) }} USD</span>
								</div>
								<div class="detail-row">
									<span class="label">{{ $t('balanceLog.frozen') || '冻结' }}</span>
									<span class="value">{{ formatBalance(accountBalances.spotFrozen) }} USD</span>
								</div>
							</div>
						</div>

						<!-- 合约账户 -->
						<div class="account-card account-card-contract">
							<div class="card-header">
								<div class="account-icon">
									<i class="el-icon-files"></i>
								</div>
								<div class="account-name">{{ $t('transfer.contractAccount') || '合约账户' }}</div>
							</div>
							<div class="account-balance">{{ formatBalance(accountBalances.contractBalance) }}</div>
							<div class="account-details">
								<div class="detail-row">
									<span class="label">{{ $t('transfer.available') || '可用' }}</span>
									<span class="value">{{ formatBalance(accountBalances.contractAvailable) }} USD</span>
								</div>
								<div class="detail-row">
									<span class="label">{{ $t('balanceLog.frozen') || '冻结' }}</span>
									<span class="value">{{ formatBalance(accountBalances.contractFrozen) }} USD</span>
								</div>
							</div>
						</div>

						<!-- 期权账户 -->
						<div class="account-card account-card-option">
							<div class="card-header">
								<div class="account-icon">
									<i class="el-icon-document-checked"></i>
								</div>
								<div class="account-name">{{ $t('transfer.optionAccount') || '期权账户' }}</div>
							</div>
							<div class="account-balance">{{ formatBalance(accountBalances.optionBalance) }}</div>
							<div class="account-details">
								<div class="detail-row">
									<span class="label">{{ $t('transfer.available') || '可用' }}</span>
									<span class="value">{{ formatBalance(accountBalances.optionAvailable) }} USD</span>
								</div>
								<div class="detail-row">
									<span class="label">{{ $t('balanceLog.frozen') || '冻结' }}</span>
									<span class="value">{{ formatBalance(accountBalances.optionFrozen) }} USD</span>
								</div>
							</div>
						</div>
					</div>

					<!-- 余额变动记录 -->
					<div class="balance-log-section">
						<BalanceChangeLog />
					</div>
				</div>
					
					<!-- 入金内容 -->
					<div v-show="activeTab === 'deposit'" class="content-panel">
						<h3 class="panel-title">{{ $t('account.deposit') || '入金' }}</h3>
						
						<!-- 选择网络 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.selectNetwork') || '选择网络' }}</div>
							<el-select 
								v-model="depositForm.currencyIndex" 
								@change="handleCurrencyChange" 
								placeholder="请选择网络"
								popper-append-to-body>
								<el-option 
									v-for="(item, index) in rechargeAccount" 
									:key="index"
									:label="item.currency_name"
									:value="index"
								></el-option>
							</el-select>
						</div>
						
						<!-- 钱包地址 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.walletAddress') || '钱包地址' }}</div>
							<div class="address-input-group">
								<input 
									type="text" 
									readonly 
									:value="currentDepositAddress" 
									class="address-input"
								>
								<button @click="copyDepositAddress" class="copy-btn">
									<i class="el-icon-document-copy"></i>
								</button>
							</div>
						</div>
						
						<!-- 二维码 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.qrCode') || '二维码' }}</div>
							<div class="qr-code-container">
								<img 
									v-if="depositQrCode"
									:key="'qr-' + depositForm.currencyIndex"
									:src="depositQrCode" 
									class="qr-code-image"
								>
							</div>
						</div>
						
						<!-- 充值数量 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.depositAmountInput') || '充值数量' }}</div>
							<input 
								type="number" 
								v-model="depositForm.amount" 
								:placeholder="$t('account.enterDepositAmount') || '请输入充值数量'" 
								class="form-input"
							>
						</div>
						
						<!-- 上传凭证 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.uploadProof') || '上传凭证' }}</div>
							<div class="upload-area" @click="triggerDepositUpload">
								<input 
									ref="depositProofInput"
									type="file" 
									accept="image/*" 
									@change="handleDepositProofUpload"
									style="display: none;"
								>
								<div v-if="!depositForm.proofUrl" class="upload-placeholder">
									<i class="el-icon-upload" style="font-size: 40px; color: #999; margin-bottom: 8px;"></i>
									<div class="upload-text">{{ $t('account.clickToUpload') || '点击上传凭证' }}</div>
									<div class="upload-hint">{{ $t('account.supportedFormats') || '支持 JPG、PNG 格式' }}</div>
								</div>
							<div v-else class="upload-preview">
								<img :src="depositForm.proofUrl" class="preview-image">
								<!-- 删除"上传成功"提示文本 -->
								<!-- <div class="upload-success">{{ $t('account.uploadSuccess') || '上传成功' }}</div> -->
							</div>
							</div>
						</div>
						
					<!-- 提交按钮 -->
					<div class="form-actions">
						<button @click="submitDeposit" class="submit-btn" :disabled="depositLoading">
							{{ depositLoading ? $t('common.submitting') : $t('account.submit') || '提交' }}
						</button>
					</div>
					
					<!-- 入金记录 -->
					<div class="record-section">
						<h4 class="record-title">{{ $t('ruJinJiLu') || '入金记录' }}</h4>
						
						<div v-if="depositHistory.length > 0" class="record-list">
							<div v-for="(record, index) in depositHistory" :key="'deposit-' + index" class="record-item">
								<div class="record-header">
									<div class="record-type">{{ record.unit }} {{ $t('account.deposit') || '充值' }}</div>
									<div class="record-status" :class="record.statusClass">{{ record.status }}</div>
								</div>
								<div class="record-details">
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.depositCryptoQty') || '充值数量' }}：</span>
										<span class="detail-value">{{ formatRechargeNum(record.amount) }} {{ record.unit }}</span>
									</div>
									<div class="detail-row" v-if="record.usdtAmount != null && record.usdtAmount !== ''">
										<span class="detail-label">{{ $t('account.depositUsdtValue') || '折合 USDC' }}：</span>
										<span class="detail-value">{{ formatRechargeNum(record.usdtAmount) }} USDC</span>
									</div>
									<div class="detail-row" v-if="record.address">
										<span class="detail-label">{{ $t('account.receiveAddress') || '充值地址' }}：</span>
										<span class="detail-value">{{ record.address }}</span>
									</div>
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.applyTime') || '申请时间' }}：</span>
										<span class="detail-value">{{ record.time }}</span>
									</div>
									<div class="detail-row" v-if="record.reason">
										<span class="detail-label">{{ $t('account.rejectReason') || '拒绝原因' }}：</span>
										<span class="detail-value error-text">{{ record.reason }}</span>
									</div>
									<div class="detail-row" v-if="record.updateTime && record.statusClass !== 'pending'">
										<span class="detail-label">{{ $t('account.processTime') || '处理时间' }}：</span>
										<span class="detail-value">{{ record.updateTime }}</span>
									</div>
								</div>
							</div>
							
							<!-- 加载更多 -->
							<div class="load-more-wrapper" v-if="depositHasMore">
								<button @click="loadMoreDeposit" class="load-more-btn" :disabled="depositLoadingMore">
									{{ depositLoadingMore ? $t('common.loading') || '加载中...' : $t('common.loadMore') || '加载更多' }}
								</button>
							</div>
							<div class="no-more-text" v-else>
								{{ $t('common.noMoreData') || '没有更多数据了' }}
							</div>
						</div>
						
						<div v-else class="empty-records">
							<i class="el-icon-document" style="font-size: 48px; color: #ddd;"></i>
							<p>{{ $t('meiYouRuJinJiLu') || '暂无入金记录' }}</p>
						</div>
					</div>
				</div>
					
					<!-- 出金内容 -->
					<div v-show="activeTab === 'withdraw'" class="content-panel">
						<h3 class="panel-title">{{ $t('account.withdraw') || '出金' }}</h3>
						
					<!-- 可用余额 -->
					<div class="balance-info-card">
						<div class="info-label">{{ $t('account.availableBalance') || '可用余额' }}</div>
						<div class="info-amount">{{ userInfo.amount || 0 }} USD</div>
					</div>
						
						<!-- 提款方式 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.withdrawalType') || '提款方式' }}</div>
							<el-select 
								v-model="withdrawForm.typeIndex" 
								@change="updateWithdrawEstimate" 
								placeholder="请选择提款方式"
								popper-append-to-body>
								<el-option 
									v-for="(type, index) in validWithdrawalTypes" 
									:key="index"
									:label="type.label"
									:value="type.value"
								></el-option>
							</el-select>
						</div>
						
						<!-- 提款金额 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.withdrawalAmount') || '提款金额' }}</div>
							<div class="amount-input-group">
								<input 
									type="number" 
									v-model="withdrawForm.amount" 
									@input="updateWithdrawEstimate"
									:placeholder="$t('account.enterWithdrawalAmount') || '请输入提款金额'" 
									class="form-input"
								>
								<button @click="setMaxWithdraw" class="max-btn">
									{{ $t('account.all') || '全部' }}
								</button>
							</div>
					</div>
					
					<!-- 提款地址（非BANK） -->
					<div class="form-row" v-if="withdrawalTypeList[withdrawForm.typeIndex] !== 'BANK'">
						<div class="form-label">{{ $t('account.withdrawalAddress') || '提款地址' }}</div>
						<input 
							type="text" 
							v-model="withdrawForm.address" 
							:placeholder="$t('account.enterWithdrawalAddress') || '请输入提款地址'" 
							class="form-input"
						>
					</div>
					
					<!-- 银行信息（BANK） -->
					<template v-if="withdrawalTypeList[withdrawForm.typeIndex] === 'BANK'">
						<!-- 银行名称 -->
						<div class="form-row">
							<div class="form-label">{{ $t('yinHangMingCheng') || '银行名称' }}</div>
							<input 
								type="text" 
								v-model="withdrawForm.bankInfo.bankName" 
								:placeholder="$t('qingShuRuYinHangMingCheng') || '请输入银行名称'" 
								class="form-input"
							>
						</div>
						
						<!-- 开户行地址 -->
						<div class="form-row">
							<div class="form-label">{{ $t('kaiHuHangDiZhi') || '开户行地址' }}</div>
							<input 
								type="text" 
								v-model="withdrawForm.bankInfo.branchAddress" 
								:placeholder="$t('qingShuRuKaiHuHangDiZhi') || '请输入开户行地址'" 
								class="form-input"
							>
						</div>
						
						<!-- 姓名 -->
						<div class="form-row">
							<div class="form-label">{{ $t('xingMing') || '姓名' }}</div>
							<input 
								type="text" 
								v-model="withdrawForm.bankInfo.accountName" 
								:placeholder="$t('qingShuRuXingMing') || '请输入姓名'" 
								class="form-input"
							>
						</div>
						
						<!-- IBAN -->
						<div class="form-row">
							<div class="form-label">IBAN</div>
							<input 
								type="text" 
								v-model="withdrawForm.bankInfo.iban" 
								:placeholder="$t('qingShuRuYinHangZhangHao') || '请输入银行账号'" 
								class="form-input"
							>
						</div>
					</template>
					
					<!-- 手续费说明 -->
						<div class="fee-info-card">
							<div class="fee-row">
								<span class="fee-label">{{ $t('account.handlingFee') || '手续费' }}</span>
								<span class="fee-value">{{ withdrawFee }}</span>
							</div>
							<div class="fee-row">
								<span class="fee-label">{{ $t('account.estimatedArrival') || '预计到账' }}</span>
								<span class="estimated-value">{{ withdrawEstimate }}</span>
							</div>
						</div>
						
					<!-- 提交按钮 -->
					<div class="form-actions">
						<button @click="submitWithdraw" class="submit-btn" :disabled="withdrawLoading">
							{{ withdrawLoading ? $t('common.submitting') : $t('account.submit') || '提交' }}
						</button>
					</div>
					
					<!-- 出金记录 -->
					<div class="record-section">
						<h4 class="record-title">{{ $t('tiBiJiLu') || '提币记录' }}</h4>
						
						<div v-if="withdrawHistory.length > 0" class="record-list">
							<div v-for="(record, index) in withdrawHistory" :key="'withdraw-' + index" class="record-item">
								<div class="record-header">
									<div class="record-type">{{ record.unit }} {{ $t('account.withdraw') || '提现' }}</div>
									<div class="record-status" :class="record.statusClass">{{ record.status }}</div>
								</div>
								<div class="record-details">
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.withdrawAmount') || '提现金额' }}：</span>
										<span class="detail-value">{{ record.amount }} USD</span>
									</div>
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.handlingFee') || '手续费' }}：</span>
										<span class="detail-value">{{ record.withdrawalFee }} USD</span>
									</div>
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.receivedAmount') || '到账金额' }}：</span>
										<span class="detail-value">{{ record.receivedAmount }} USD</span>
									</div>
								<!-- 银行信息（分行显示） -->
								<template v-if="isBankAddress(record.address)">
									<div class="detail-row" v-for="(item, idx) in parseBankInfo(record.address)" :key="'bank-' + idx">
										<span class="detail-label">{{ item.label }}：</span>
										<span class="detail-value">{{ item.value }}</span>
									</div>
								</template>
								<!-- 普通提现地址 -->
								<div class="detail-row" v-else-if="record.address">
									<span class="detail-label">{{ $t('tiXianDiZhi') || '提现地址' }}：</span>
									<span class="detail-value">{{ record.address }}</span>
								</div>
									<div class="detail-row">
										<span class="detail-label">{{ $t('account.applyTime') || '申请时间' }}：</span>
										<span class="detail-value">{{ record.time }}</span>
									</div>
									<div class="detail-row" v-if="record.reason">
										<span class="detail-label">{{ $t('account.rejectReason') || '拒绝原因' }}：</span>
										<span class="detail-value error-text">{{ record.reason }}</span>
									</div>
									<div class="detail-row" v-if="record.updateTime && record.statusClass !== 'pending'">
										<span class="detail-label">{{ $t('account.processTime') || '处理时间' }}：</span>
										<span class="detail-value">{{ record.updateTime }}</span>
									</div>
								</div>
							</div>
							
							<!-- 加载更多 -->
							<div class="load-more-wrapper" v-if="withdrawHasMore">
								<button @click="loadMoreWithdraw" class="load-more-btn" :disabled="withdrawLoadingMore">
									{{ withdrawLoadingMore ? $t('common.loading') || '加载中...' : $t('common.loadMore') || '加载更多' }}
								</button>
							</div>
							<div class="no-more-text" v-else>
								{{ $t('common.noMoreData') || '没有更多数据了' }}
							</div>
						</div>
						
						<div v-else class="empty-records">
							<i class="el-icon-document" style="font-size: 48px; color: #ddd;"></i>
							<p>{{ $t('meiYouTiBiJiLu') || '暂无提币记录' }}</p>
						</div>
					</div>
				</div>
					
					<!-- KYC实名认证 -->
					<div v-show="activeTab === 'kyc'" class="content-panel">
						<h3 class="panel-title">{{ $t('loan.notVerifyBtn') || '实名认证' }}</h3>
						
					<!-- 已认证状态 -->
					<div v-if="isKycVerified" class="kyc-status-display">
						<div class="status-icon-wrapper verified">
							<svg viewBox="0 0 1024 1024" width="80" height="80">
								<path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 0 1-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z" fill="#67c23a"/>
							</svg>
						</div>
						<h3 class="status-title verified">{{ $t('account.verified') || '已认证' }}</h3>
					</div>
						
					<!-- 审核中状态 -->
					<div v-else-if="isKycPending" class="kyc-status-display">
						<div class="status-icon-wrapper pending">
							<svg viewBox="0 0 1024 1024" width="80" height="80">
								<path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" fill="var(--primary-color)"/>
								<path d="M464 336a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm72 112h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V456c0-4.4-3.6-8-8-8z" fill="var(--primary-color)"/>
							</svg>
						</div>
						<h3 class="status-title pending">{{ $t('loan.pendingReview') || '审核中' }}</h3>
					</div>
						
						<!-- KYC表单（未认证或认证失败） -->
						<div v-else>
						<div class="kyc-form-grid">
							<!-- 名字 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.firstName') || '名字' }}</label>
								<input type="text" v-model="kycForm.firstname" class="form-group-input">
							</div>
							
							<!-- 姓氏 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.lastName') || '姓氏' }}</label>
								<input type="text" v-model="kycForm.lastname" class="form-group-input">
							</div>
							
							<!-- 性别 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.sex') || '性别' }}</label>
								<select v-model="kycForm.sex" class="form-group-input">
									<option value="1">{{ $t('loan.male') || '男' }}</option>
									<option value="0">{{ $t('loan.female') || '女' }}</option>
								</select>
							</div>
							
							<!-- 生日 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.birthdate') || '生日' }}</label>
								<input type="date" v-model="kycForm.birthday" class="form-group-input">
							</div>
							
							<!-- 国家 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.country') || '国家' }}</label>
								<input type="text" v-model="kycForm.country" class="form-group-input">
							</div>
							
							<!-- 地址 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.address') || '地址' }}</label>
								<input type="text" v-model="kycForm.address" class="form-group-input">
							</div>
							
							<!-- 电话号码 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('account.phoneNumber') || '电话号码' }}</label>
								<div class="phone-input-group">
									<select v-model="kycForm.phoneCode" class="phone-code-select">
										<option v-for="code in phoneCodeOptions" :key="code" :value="code">{{ code }}</option>
									</select>
									<input 
										type="tel" 
										v-model="kycForm.phone" 
										:placeholder="$t('account.enterPhoneNumber') || '请输入电话号码'" 
										class="phone-input"
									>
								</div>
							</div>
							
							<!-- 证件类型 -->
							<div class="form-group">
								<label class="form-group-label">{{ $t('loan.document') || '证件类型' }}</label>
								<select v-model="kycForm.doctype" class="form-group-input">
									<option value="1">{{ $t('loan.idCard') || '身份证' }}</option>
									<option value="2">{{ $t('loan.passport') || '护照' }}</option>
									<option value="3">{{ $t('loan.driverLicense') || '驾照' }}</option>
								</select>
							</div>
						</div>
						
						<!-- 上传证件照片 -->
						<h4 class="upload-title">{{ $t('loan.uploadDocuments') || '上传证件照片' }}</h4>
						<div class="kyc-upload-grid">
							<!-- 证件正面 -->
							<div class="kyc-upload-item">
								<label class="upload-item-label">{{ $t('loan.tipIdCardFront') || '证件正面' }}</label>
								<div class="kyc-upload-box" @click="triggerKycUpload('front')">
									<input 
										ref="kycFrontInput"
										type="file" 
										accept="image/*" 
										@change="handleKycUpload($event, 'front')"
										style="display: none;"
									>
									<div v-if="!kycForm.frontImg" class="upload-box-placeholder">
										<i class="el-icon-upload" style="font-size: 48px; color: #999;"></i>
										<div class="upload-box-text">{{ $t('common.clickToUpload') || '点击上传' }}</div>
									</div>
									<img v-else :src="kycForm.frontImg" class="kyc-preview-image">
								</div>
							</div>
							
							<!-- 证件反面 -->
							<div class="kyc-upload-item">
								<label class="upload-item-label">{{ $t('loan.tipIdCardBack') || '证件反面' }}</label>
								<div class="kyc-upload-box" @click="triggerKycUpload('back')">
									<input 
										ref="kycBackInput"
										type="file" 
										accept="image/*" 
										@change="handleKycUpload($event, 'back')"
										style="display: none;"
									>
									<div v-if="!kycForm.backImg" class="upload-box-placeholder">
										<i class="el-icon-upload" style="font-size: 48px; color: #999;"></i>
										<div class="upload-box-text">{{ $t('common.clickToUpload') || '点击上传' }}</div>
									</div>
									<img v-else :src="kycForm.backImg" class="kyc-preview-image">
								</div>
							</div>
						</div>
						
						<!-- 提交按钮 -->
						<div class="form-actions" style="margin-top: 30px;">
							<button @click="submitKyc" class="submit-btn" :disabled="kycLoading">
								{{ kycLoading ? $t('common.submitting') : $t('loan.submit') || '提交' }}
							</button>
						</div>
						</div>
					</div>
					
					<!-- 邀请好友 -->
					<div v-show="activeTab === 'invite'" class="content-panel">
						<h3 class="panel-title">{{ $t('account.inviteFriends') || '邀请好友' }}</h3>
						
						<!-- 邀请码显示 -->
						<div class="invite-code-display">
							<div class="invite-code-label">{{ $t('account.inviteCode') || '邀请码' }}</div>
							<div class="invite-code-value">{{ userInfo.inviteNumber || 'yms0' }}</div>
						</div>
						
						<!-- 二维码显示 -->
						<div class="invite-qr-display">
							<img :src="inviteQrCode" class="invite-qr-image">
						</div>
						
						<!-- 邀请链接 -->
						<div class="invite-link-card">
							<div class="invite-link-label">{{ $t('account.inviteLink') || '邀请链接' }}</div>
							<div class="invite-link-value">{{ inviteLink }}</div>
						</div>
						
						<!-- 复制链接按钮 -->
						<div class="form-actions">
							<button @click="copyInviteLink" class="submit-btn">
								{{ $t('account.copyInviteLink') || '复制邀请链接' }}
							</button>
						</div>
					</div>
					
					<!-- 修改密码 -->
					<div v-show="activeTab === 'password'" class="content-panel">
						<h3 class="panel-title">{{ $t('account.password') || '修改密码' }}</h3>
						
						<!-- 手机号（只读） -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.phoneNumber') || '手机号' }}</div>
							<input 
								type="text" 
								:value="userInfo.phone || ''" 
								readonly 
								class="form-input readonly-input"
							>
						</div>
						
						<!-- 新密码 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.newPassword') || '新密码' }}</div>
							<input 
								type="password" 
								v-model="passwordForm.newPassword" 
								:placeholder="$t('account.enterNewPassword') || '请输入新密码'" 
								class="form-input"
							>
							<div class="form-hint">{{ $t('account.passwordLengthHint') || '密码长度至少6位' }}</div>
						</div>
						
						<!-- 确认密码 -->
						<div class="form-row">
							<div class="form-label">{{ $t('account.confirmPassword') || '确认密码' }}</div>
							<input 
								type="password" 
								v-model="passwordForm.confirmPassword" 
								:placeholder="$t('account.enterConfirmPassword') || '请再次输入密码'" 
								class="form-input"
							>
						</div>
						
						<!-- 提交按钮 -->
						<div class="form-actions" style="margin-top: 30px;">
							<button @click="savePassword" class="submit-btn" :disabled="passwordLoading">
								{{ passwordLoading ? $t('common.submitting') : '保存' }}
							</button>
						</div>
					</div>
					
					<!-- 联系客服 -->
					<div v-show="activeTab === 'support'" class="content-panel">
						<h3 class="panel-title">{{ $t('account.support') || '联系客服' }}</h3>
						
						<!-- WhatsApp服务 -->
						<div class="support-card" @click="openWhatsApp">
							<div class="support-card-content">
								<div class="support-icon whatsapp-icon">
									<i class="el-icon-phone" style="font-size: 28px;"></i>
								</div>
								<div class="support-info">
									<div class="support-title">WhatsApp Service</div>
									<!-- <div class="support-subtitle">+16236988384</div> -->
								</div>
							</div>
							<i class="el-icon-arrow-right support-arrow"></i>
						</div>
						
						<!-- 在线客服 -->
						<div class="support-card" @click="openOnlineService">
							<div class="support-card-content">
								<div class="support-icon online-icon">
									<i class="el-icon-chat-dot-round" style="font-size: 28px;"></i>
								</div>
								<div class="support-info">
									<div class="support-title">Online Service</div>
								</div>
							</div>
							<i class="el-icon-arrow-right support-arrow"></i>
						</div>
					</div>
					
				<!-- 公告 -->
				<div v-show="activeTab === 'notice'" class="content-panel">
					<h3 class="panel-title">{{ $t('account.notice') || '公告' }}</h3>
					
					<!-- 加载状态 -->
					<div v-if="noticeLoading" class="loading-state">
						<i class="el-icon-loading" style="font-size: 48px; color: var(--primary-color, #0156DC); margin-bottom: 15px;"></i>
						<div class="loading-text">{{ $t('common.loading') || '加载中...' }}</div>
					</div>
					
				<!-- 通知列表 -->
				<div v-else-if="noticeList.length > 0" class="notice-list">
					<div v-for="item in noticeList" :key="item.id" class="notice-item">
						<div class="notice-header">
							<!-- <div class="notice-title">{{ item.title }}</div> -->
							<div class="notice-date">{{ formatNoticeDate(item.createTime) }}</div>
						</div>
						<!-- 直接显示完整的info内容，不需要点击 -->
						<div class="notice-content" v-html="item.info"></div>
					</div>
				</div>
					
					<!-- 暂无数据 -->
					<div v-else class="empty-state">
						<i class="el-icon-document" style="font-size: 64px; color: #ddd; margin-bottom: 20px;"></i>
						<div class="empty-text">{{ $t('toast.noData') || '暂无数据' }}</div>
					</div>
				</div>
				</div>
			</div>
		</div>
		
	<!-- 成功提示弹窗 -->
	<SuccessModal ref="successModal" />
</div>
</template>

<script>
import { repassword } from '@/api/user.js';
import SuccessModal from '@/components/SuccessModal.vue';
import BalanceChangeLog from '@/components/BalanceChangeLog.vue';
import { mapState } from 'vuex';

export default {
	name: 'AccountCenter',
	components: {
		SuccessModal,
		BalanceChangeLog
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		userInfo: {
			type: Object,
			default: () => ({})
		},
		rechargeAccount: {
			type: Array,
			default: () => []
		},
		withdrawalTypeList: {
			type: Array,
			default: () => []
		},
		withdrawalFee: {
			type: Object,
			default: () => ({})
		},
		virtualStatus: {
			type: Number,
			default: 1
		}
	},
	data() {
		return {
			activeTab: 'wallet',
			
			// 账户余额
			accountBalances: {
				spotBalance: 0,
				spotFrozen: 0,
				spotAvailable: 0,
				contractBalance: 0,
				contractFrozen: 0,
				contractAvailable: 0,
				optionBalance: 0,
				optionFrozen: 0,
				optionAvailable: 0
			},
			
		// 充值表单
		depositForm: {
			currencyIndex: 0,
			amount: '',
			proofUrl: ''
		},
		depositLoading: false,
		depositHistory: [],
		depositPage: 1,
		depositPageSize: 10,
		depositHasMore: true,
		depositLoadingMore: false,
		
	// 提现表单
	withdrawForm: {
		typeIndex: 0,
		amount: '',
		address: '',
		bankInfo: {
			bankName: '',
			branchAddress: '',
			accountName: '',
			iban: ''
		}
	},
		withdrawLoading: false,
		withdrawHistory: [],
		withdrawPage: 1,
		withdrawPageSize: 10,
		withdrawHasMore: true,
		withdrawLoadingMore: false,
			
			// KYC表单
			kycForm: {
				firstname: '',
				lastname: '',
				sex: '1',
				birthday: '',
				country: '',
				address: '',
				phoneCode: '+86',
				phone: '',
				doctype: '1',
				frontImg: '',
				backImg: ''
			},
			kycLoading: false,
			
			// 密码表单
			passwordForm: {
				newPassword: '',
				confirmPassword: ''
			},
			passwordLoading: false,
			
		// 客服配置
		customerServiceConfig: {
			whatsappLink: 'https://wa.me/16236988384',  // 默认WhatsApp链接
			whatsappNumber: '+16236988384'               // 默认WhatsApp号码
		},
		
	// 公告通知
	noticeList: [],
	noticeLoading: false
};
},
	computed: {
		...mapState(['areaCodes']),
		// 区号列表 - 使用 Vuex store 中的统一数据
		phoneCodeOptions() {
			return this.areaCodes || [];
		},
		menuItems() {
			return [
				{ tab: 'wallet', label: this.$t('account.wallet') || '钱包' },
				{ tab: 'deposit', label: this.$t('account.deposit') || '入金' },
				{ tab: 'withdraw', label: this.$t('account.withdraw') || '出金' },
				{ tab: 'kyc', label: this.$t('account.kyc') || '实名认证' },
				{ tab: 'invite', label: this.$t('account.inviteFriends') || '邀请好友' },
				{ tab: 'password', label: this.$t('account.password') || '修改密码' },
				{ tab: 'support', label: this.$t('account.support') || '联系客服' },
				{ tab: 'notice', label: this.$t('account.notice') || '公告' },
				{ tab: 'logout', label: this.$t('account.logout') || '退出登录' }
			];
		},
		// 获取当前语言ID
		currentLanguage() {
			const lang = localStorage.getItem('lang') || 'zh-CN';
			const languageMap = {
				'zh-CN': 1,   // 简体中文
				'en': 2,      // English
				'zh-HK': 3,   // 繁体中文
				'ja': 4,      // 日本語
				'ko': 5       // 한국어
			};
			return languageMap[lang] || 1;
		},
		// ✅ 新增：检测是否为模拟账户
		isDemoAccount() {
			const isDemo = this.userInfo && (this.userInfo.isDemo === 1 || this.userInfo.isDemo === '1');
			return isDemo;
		},
		// KYC认证状态
		kycStatus() {
			return this.userInfo.idefinderStatus;
		},
		isKycVerified() {
			return this.kycStatus == 1; // 已认证
		},
		isKycPending() {
			return this.kycStatus === 0; // 审核中
		},
		isKycUnverified() {
			return this.kycStatus !== 0 && this.kycStatus != 1; // 未认证或认证失败
		},
		// 过滤掉空的提款方式，并保持原始索引
		validWithdrawalTypes() {
			return this.withdrawalTypeList
				.map((type, index) => ({ label: type, value: index }))
				.filter(item => item.label && item.label.trim() !== '');
		},
		currentDepositAddress() {
			const account = this.rechargeAccount[this.depositForm.currencyIndex];
			return account?.address || '';
		},
		depositQrCode() {
			const account = this.rechargeAccount[this.depositForm.currencyIndex];
			if (!account || !account.qr_code) {
				return '';
			}
			
			let qrCode = account.qr_code;
			
			// 对URL进行编码处理，处理中文字符和空格
			try {
				qrCode = encodeURI(qrCode);
			} catch (e) {
				console.error('URL编码失败:', e);
			}
			
			return qrCode;
		},
		inviteLink() {
			const code = this.userInfo.inviteNumber || 'yms0';
			return `${window.location.origin}/#/pass/register?code=${code}`;
		},
		inviteQrCode() {
			return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(this.inviteLink)}`;
		},
		withdrawFee() {
			const amount = parseFloat(this.withdrawForm.amount) || 0;
			if (amount <= 0) return '-';
			
			const feeRatePercent = parseFloat(this.withdrawalFee?.paramValue || 0.025);
			const feeRate = feeRatePercent / 100;
			const fee = amount * feeRate;
			return fee;
		},
		withdrawEstimate() {
			const amount = parseFloat(this.withdrawForm.amount) || 0;
			if (amount <= 0) return '- USD';
			
			const fee = parseFloat(this.withdrawFee);
			const estimate = amount - fee;
			return estimate + ' USD';
		}
	},
	mounted() {
		this.fetchAccountBalances();
	},
	methods: {
		// 获取账户余额
		async fetchAccountBalances() {
			try {
				const res = await this.$http.get('/swx/customer/account/balances');
				if (res.code === 200 && res.result) {
					const data = res.result;
					this.accountBalances = {
						spotBalance: parseFloat(data.spotBalance || 0),
						spotFrozen: parseFloat(data.spotFrozen || 0),
						spotAvailable: parseFloat(data.spotAvailable || 0),
						contractBalance: parseFloat(data.contractBalance || 0),
						contractFrozen: parseFloat(data.contractFrozen || 0),
						contractAvailable: parseFloat(data.contractAvailable || 0),
						optionBalance: parseFloat(data.optionBalance || 0),
						optionFrozen: parseFloat(data.optionFrozen || 0),
						optionAvailable: parseFloat(data.optionAvailable || 0)
					};
				}
			} catch (error) {
				console.error('获取账户余额失败:', error);
			}
		},
		
		// 格式化余额
		formatBalance(value) {
			return parseFloat(value) || 0;
		},
		
		handleClose() {
			this.$emit('update:visible', false);
			this.$emit('close');
		},
		
	handleMenuClick(tab) {
		// ✅ 退出登录不需要检查
		if (tab === 'logout') {
			this.$emit('logout');
			return;
		}
		
		// ✅ 检查是否为模拟账户（钱包和退出登录除外）
		if (tab !== 'wallet' && this.isDemoAccount) {
			this.$message.warning(this.$t('account.demoAccountOperationForbidden') || '模拟账户禁止操作');
			return;
		}
		
	// 如果点击KYC tab，检查认证状态
	if (tab === 'kyc') {
		// 不显示任何提示，直接切换到KYC tab显示状态
	}
		
		// 切换到钱包tab时刷新余额
		if (tab === 'wallet') {
			this.fetchAccountBalances();
		}
		
		// 切换到入金tab时加载入金记录
		if (tab === 'deposit' && this.depositHistory.length === 0) {
			this.loadDepositHistory();
		}
		
		// 切换到出金tab时加载出金记录
		if (tab === 'withdraw' && this.withdrawHistory.length === 0) {
			this.loadWithdrawHistory();
		}
		
		// 切换到公告tab时加载通知列表
		if (tab === 'notice' && this.noticeList.length === 0) {
			this.loadNoticeList();
		}
		
		this.activeTab = tab;
	},
		
		// ========== 充值相关方法 ==========
		handleCurrencyChange() {
			// 货币切换时，二维码和地址会自动更新（通过computed）
		},
		
		copyDepositAddress() {
			const address = this.currentDepositAddress;
			if (!address) {
				this.$toast.warning('暂无地址');
				return;
			}
			
			navigator.clipboard.writeText(address).then(() => {
				this.$toast.success('地址已复制');
			}).catch(() => {
				this.$toast.error('复制失败');
			});
		},
		
		triggerDepositUpload() {
			this.$refs.depositProofInput.click();
		},
		
		async handleDepositProofUpload(event) {
			const file = event.target.files[0];
			if (!file) return;
			
			// 验证文件类型
			if (!file.type.startsWith('image/')) {
				this.$toast.error(this.$t('account.pleaseUploadImage') || '请上传图片文件');
				return;
			}
			
			// 验证文件大小（5MB）
			if (file.size > 5 * 1024 * 1024) {
				this.$toast.error(this.$t('account.fileTooLarge') || '图片大小不能超过5MB');
				return;
			}
			
		try {
			const formData = new FormData();
			formData.append('file', file);
			
			// 删除"上传中"提示
			// const loadingMsg = this.$toast({
			// 	message: this.$t('common.uploading') || '上传中...',
			// 	type: 'info',
			// 	duration: 0
			// });
			
			const response = await this.$http.post('/common/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' }
			});
			
			// loadingMsg.close();
			
			if (response.code === 200) {
				this.depositForm.proofUrl = response.url || response.data?.url || response.fileName;
				// 删除"上传成功"提示
				// this.$toast.success(this.$t('account.uploadSuccess') || '上传成功');
			} else {
				throw new Error(response.msg || '上传失败');
			}
		} catch (error) {
			console.error('❌ 上传凭证失败:', error);
			this.$toast.error(this.$t('account.uploadFailed') || '上传失败');
		}
			
			event.target.value = '';
		},
		
		async submitDeposit() {
			// 验证
			if (!this.depositForm.amount || this.depositForm.amount <= 0) {
				this.$toast.error(this.$t('account.pleaseEnterAmount') || '请输入充值数量');
				return;
			}
			
			if (!this.depositForm.proofUrl) {
				this.$toast.error(this.$t('account.pleaseUploadProof') || '请上传充值凭证');
				return;
			}
			
			const selectedAccount = this.rechargeAccount[this.depositForm.currencyIndex];
			if (!selectedAccount) {
				this.$toast.error('请选择充值网络');
				return;
			}
			
			try {
				this.depositLoading = true;
				
				const response = await this.$http.post('/swx/customer/user/recharge', {
					type: selectedAccount.currency_name,
					amount: this.depositForm.amount,
					prove: this.depositForm.proofUrl,
					chargeAddress: selectedAccount.address,
				});
				
		if (response.code === 200) {
			// 使用SuccessModal组件显示成功提示
			this.$refs.successModal.show(this.$t('common.operationSuccess') || '操作成功');
			
			// 重置表单
			this.depositForm.amount = '';
			this.depositForm.proofUrl = '';
			
			// 刷新用户信息和入金记录
			this.$store.dispatch('GET_USER_INFO');
			this.loadDepositHistory();
			this.$emit('refresh-deposit');
		} else {
			// throw new Error(response.msg || '提交失败');
			console.error('❌ 提交充值失败:', response.msg || '提交失败');
		}
		} catch (error) {
			console.error('❌ 提交充值失败:', error);
			// this.$toast.error(this.$t('account.submitFailed') || '提交失败');
		} finally {
				this.depositLoading = false;
			}
		},
		
		// ========== 提现相关方法 ==========
		setMaxWithdraw() {
			this.withdrawForm.amount = this.userInfo.amount || 0;
			this.updateWithdrawEstimate();
		},
		
		updateWithdrawEstimate() {
			// 自动计算手续费和到账金额（通过computed）
		},
		
		async submitWithdraw() {
			// 验证
			if (this.virtualStatus != 1) {
				this.$toast.warning(this.$t('account.demoAccountRestriction') || '模拟账户不能使用该功能');
				return;
			}
			
			if (!this.withdrawForm.amount || this.withdrawForm.amount <= 0) {
				this.$toast.warning(this.$t('account.enterWithdrawalAmount') || '请输入提款金额');
				return;
			}
			
		if (parseFloat(this.withdrawForm.amount) > parseFloat(this.userInfo.amount || 0)) {
			this.$toast.warning(this.$t('account.insufficientBalance') || '余额不足');
			return;
		}
		
		const withdrawalType = this.withdrawalTypeList[this.withdrawForm.typeIndex];
		let withdrawAddress = this.withdrawForm.address;
		
		// 验证地址（非银行转账需要地址）
		if (withdrawalType !== 'BANK') {
			if (!this.withdrawForm.address) {
				this.$toast.warning(this.$t('account.enterWithdrawalAddress') || '请输入提款地址');
				return;
			}
		} else {
			// 验证银行信息
			if (!this.withdrawForm.bankInfo.bankName) {
				this.$toast.warning(this.$t('qingShuRuYinHangMingCheng') || '请输入银行名称');
				return;
			}
			if (!this.withdrawForm.bankInfo.branchAddress) {
				this.$toast.warning(this.$t('qingShuRuKaiHuHangDiZhi') || '请输入开户行地址');
				return;
			}
			if (!this.withdrawForm.bankInfo.accountName) {
				this.$toast.warning(this.$t('qingShuRuXingMing') || '请输入姓名');
				return;
			}
			if (!this.withdrawForm.bankInfo.iban) {
				this.$toast.warning(this.$t('qingShuRuYinHangZhangHao') || '请输入银行账号');
				return;
			}
			// 将银行信息拼接成地址字段（带字段名称）
			withdrawAddress = `银行名称:${this.withdrawForm.bankInfo.bankName}|开户行地址:${this.withdrawForm.bankInfo.branchAddress}|姓名:${this.withdrawForm.bankInfo.accountName}|IBAN:${this.withdrawForm.bankInfo.iban}`;
		}
		
		try {
			this.withdrawLoading = true;
			
			const response = await this.$http.post('/swx/customer/user/withdrawal', {
				amount: parseFloat(this.withdrawForm.amount),
				btcAddress: withdrawAddress || '',
				withdrawalType: this.withdrawForm.typeIndex
			});
				
	if (response.code === 200) {
		// 使用SuccessModal组件显示成功提示
		this.$refs.successModal.show(this.$t('common.operationSuccess') || '操作成功');
		
		// 重置表单
		this.withdrawForm.amount = '';
		this.withdrawForm.address = '';
		this.withdrawForm.bankInfo = {
			bankName: '',
			branchAddress: '',
			accountName: '',
			iban: ''
		};
		
		// 刷新用户信息和出金记录
		this.$store.dispatch('GET_USER_INFO');
		this.loadWithdrawHistory();
		this.$emit('refresh-withdraw');
	} else {
			// throw new Error(response.message || '提交失败');
			console.error('❌ 提款失败:', response.message || '提交失败');
		}
		} catch (error) {
			console.error('❌ 提款失败:', error);
			// this.$toast.error(this.$t('common.submitFailed') || '提交失败');
		} finally {
				this.withdrawLoading = false;
			}
	},
	
	// ========== 入金记录相关方法 ==========
	formatRechargeNum(n) {
		if (n == null || n === '') return '0';
		const x = Number(n);
		if (Number.isNaN(x)) return String(n);
		const s = x.toFixed(8).replace(/\.?0+$/, '');
		return s === '' ? '0' : s;
	},
	async loadDepositHistory(reset = true) {
		if (reset) {
			this.depositPage = 1;
			this.depositHistory = [];
			this.depositHasMore = true;
		}
		
		try {
			const res = await this.$http.get('/swx/customer/user/listRecharge', {
				params: {
					pageNo: this.depositPage,
					pageSize: this.depositPageSize
				}
			});
			
			if (res && res.code === 200 && res.result && res.result.records) {
				const records = res.result.records.map(record => {
					// 状态映射
					let statusText = '';
					let statusClass = '';
					switch(record.status) {
						case 0:
							statusText = this.$t('account.pending') || '待审核';
							statusClass = 'pending';
							break;
						case 1:
							statusText = this.$t('account.completed') || '已完成';
							statusClass = 'success';
							break;
						case 2:
							statusText = this.$t('account.rejected') || '已拒绝';
							statusClass = 'rejected';
							break;
						default:
							statusText = this.$t('account.unknown') || '未知';
							statusClass = 'unknown';
					}
					
					return {
						amount: record.amount || 0,
						unit: (record.type || record.unit || 'USDT').trim(),
						usdtAmount: record.currency,
						status: statusText,
						statusClass: statusClass,
						time: record.createTime || '',
						reason: record.reason || '',
						updateTime: record.updateTime || '',
						address: record.chargeAddress || record.address || ''
					};
				});
				
				if (reset) {
					this.depositHistory = records;
				} else {
					this.depositHistory = [...this.depositHistory, ...records];
				}
				
				// 检查是否还有更多数据
				this.depositHasMore = records.length === this.depositPageSize;
			} else {
				if (reset) {
					this.depositHistory = [];
				}
				this.depositHasMore = false;
			}
		} catch (error) {
			console.error('加载入金记录失败:', error);
			if (reset) {
				this.depositHistory = [];
			}
			this.depositHasMore = false;
		} finally {
			this.depositLoadingMore = false;
		}
	},
	
	loadMoreDeposit() {
		if (this.depositHasMore && !this.depositLoadingMore) {
			this.depositLoadingMore = true;
			this.depositPage++;
			this.loadDepositHistory(false);
		}
	},
	
	// ========== 出金记录相关方法 ==========
	async loadWithdrawHistory(reset = true) {
		if (reset) {
			this.withdrawPage = 1;
			this.withdrawHistory = [];
			this.withdrawHasMore = true;
		}
		
		try {
			const res = await this.$http.get('/swx/customer/user/listWithdrwal', {
				params: {
					pageNo: this.withdrawPage,
					pageSize: this.withdrawPageSize
				}
			});
			
			if (res.code === 200 && res.result && res.result.records) {
				const records = res.result.records.map(record => {
					// 状态映射
					let statusText = '';
					let statusClass = '';
					switch(record.status) {
						case 0:
							statusText = this.$t('account.pending') || '待审核';
							statusClass = 'pending';
							break;
						case 1:
							statusText = this.$t('account.completed') || '已完成';
							statusClass = 'success';
							break;
						case 2:
							statusText = this.$t('account.rejected') || '已拒绝';
							statusClass = 'rejected';
							break;
						default:
							statusText = this.$t('account.unknown') || '未知';
							statusClass = 'unknown';
					}
					
					// 计算手续费和到账金额
					const amount = parseFloat(record.amount || 0);
					const fee = parseFloat(((record.withdrawalFee !== undefined && record.withdrawalFee !== null) ? record.withdrawalFee : record.fee) || 0);
					const receivedAmount = amount - fee;
					
					return {
						amount: amount,
						fee: fee,
						withdrawalFee: fee,
						receivedAmount: receivedAmount,
						unit: record.unit || 'USDT',
						status: statusText,
						statusClass: statusClass,
						time: record.createTime || '',
						reason: record.reason || '',
						updateTime: record.updateTime || '',
						address: record.btcAddress || record.address || ''
					};
				});
				
				if (reset) {
					this.withdrawHistory = records;
				} else {
					this.withdrawHistory = [...this.withdrawHistory, ...records];
				}
				
				// 检查是否还有更多数据
				this.withdrawHasMore = records.length === this.withdrawPageSize;
			} else {
				if (reset) {
					this.withdrawHistory = [];
				}
				this.withdrawHasMore = false;
			}
		} catch (error) {
			console.error('加载出金记录失败:', error);
			if (reset) {
				this.withdrawHistory = [];
			}
			this.withdrawHasMore = false;
		} finally {
			this.withdrawLoadingMore = false;
		}
	},
	
	loadMoreWithdraw() {
		if (this.withdrawHasMore && !this.withdrawLoadingMore) {
			this.withdrawLoadingMore = true;
			this.withdrawPage++;
			this.loadWithdrawHistory(false);
		}
	},
	
	// ========== KYC相关方法 ==========
	triggerKycUpload(type) {
			if (type === 'front') {
				this.$refs.kycFrontInput.click();
			} else {
				this.$refs.kycBackInput.click();
			}
		},
		
		async handleKycUpload(event, type) {
			const file = event.target.files[0];
			if (!file) return;
			
			// 验证文件
			if (!file.type.startsWith('image/')) {
				this.$toast.warning(this.$t('common.uploadImageFile') || '请上传图片文件');
				return;
			}
			
			if (file.size > 5 * 1024 * 1024) {
				this.$toast.warning(this.$t('common.imageSizeLimit') || '图片大小不能超过5MB');
				return;
			}
			
		try {
			const formData = new FormData();
			formData.append('file', file);
			
			// 删除"上传中"提示
			// const loadingMsg = this.$toast({
			// 	message: this.$t('common.uploading') || '上传中...',
			// 	type: 'info',
			// 	duration: 0
			// });
			
			const response = await this.$http.post('/swx/customer/user/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' }
			});
			
			// loadingMsg.close();
			
			if (response.code === 200 && response.url) {
				if (type === 'front') {
					this.kycForm.frontImg = response.url;
				} else {
					this.kycForm.backImg = response.url;
				}
				// 删除"上传成功"提示
				// this.$toast.success(this.$t('common.uploadSuccess') || '上传成功');
			} else {
				throw new Error(response.msg || '上传失败');
			}
		} catch (error) {
			console.error('❌ 图片上传失败:', error);
			this.$toast.error(this.$t('common.uploadFailed') || '上传失败');
		}
			
			event.target.value = '';
		},
		
		async submitKyc() {
			// 验证必填字段
			if (!this.kycForm.firstname || !this.kycForm.lastname || !this.kycForm.birthday || 
				!this.kycForm.country || !this.kycForm.address || !this.kycForm.phone) {
				this.$toast.warning(this.$t('account.fillCompleteInfo') || '请填写完整信息');
				return;
			}
			
			// 验证手机号
			if (this.kycForm.phone.length < 6 || !/^\d+$/.test(this.kycForm.phone)) {
				this.$toast.warning(this.$t('account.enterValidPhone') || '请填写正确的电话号码');
				return;
			}
			
			// 验证照片
			if (!this.kycForm.frontImg || !this.kycForm.backImg) {
				this.$toast.warning(this.$t('account.uploadIDPhotos') || '请上传证件正反面照片');
				return;
			}
			
			try {
				this.kycLoading = true;
				
				const fullPhone = this.kycForm.phoneCode.replace('+', '') + this.kycForm.phone;
				
				const response = await this.$http.post('/swx/customer/user/identification', {
					name: this.kycForm.firstname,
					surname: this.kycForm.lastname,
					sex: this.kycForm.sex,
					birthday: this.kycForm.birthday,
					country: this.kycForm.country,
					address: this.kycForm.address,
					phone: fullPhone,
					idType: this.kycForm.doctype,
					frontImg: this.kycForm.frontImg,
					reverseImg: this.kycForm.backImg
				});
				
			if (response.code === 200) {
				// 使用SuccessModal组件显示成功提示
				this.$refs.successModal.show(this.$t('tiJiaoChengGong') || '提交成功');
				
				// 重置表单
				this.kycForm = {
					firstname: '',
					lastname: '',
					sex: '1',
					birthday: '',
					country: '',
					address: '',
					phoneCode: '+86',
					phone: '',
					doctype: '1',
					frontImg: '',
					backImg: ''
				};
				
				// 刷新用户信息
				this.$store.dispatch('GET_USER_INFO');
			} else {
				// throw new Error(response.message || '提交失败');
				console.error('❌ KYC提交失败:', response.message || '提交失败');
			}
		} catch (error) {
			console.error('❌ KYC提交失败:', error);
			// this.$toast.error(this.$t('common.submitFailed') || '提交失败');
		} finally {
				this.kycLoading = false;
			}
		},
		
		// ========== 邀请相关方法 ==========
		copyInviteLink() {
			navigator.clipboard.writeText(this.inviteLink).then(() => {
				this.$toast.success(this.$t('account.inviteLinkCopied') || '邀请链接已复制');
			}).catch(() => {
				this.$toast.error('复制失败');
			});
		},
		
		// ========== 密码相关方法 ==========
		async savePassword() {
			// 验证
			if (!this.passwordForm.newPassword) {
				this.$toast.error(this.$t('account.passwordRequired') || '请输入新密码');
				return;
			}
			
			if (this.passwordForm.newPassword.length < 6) {
				this.$toast.error(this.$t('account.passwordTooShort') || '密码长度至少6位');
				return;
			}
			
			if (!this.passwordForm.confirmPassword) {
				this.$toast.error(this.$t('account.confirmPasswordRequired') || '请确认密码');
				return;
			}
			
			if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
				this.$toast.error(this.$t('account.passwordMismatch') || '两次密码不一致');
				return;
			}
			
			try {
				this.passwordLoading = true;
				
				const response = await repassword({
					password: this.passwordForm.newPassword,
					rePaossword: this.passwordForm.confirmPassword
				});
				
			if (response.code == '200') {
				// 使用SuccessModal组件显示成功提示
				this.$refs.successModal.show(this.$t('account.passwordChangeSuccess') || '密码修改成功');
				
				// 重置表单
				this.passwordForm.newPassword = '';
				this.passwordForm.confirmPassword = '';
				
				// 延迟关闭
				setTimeout(() => {
					this.handleClose();
				}, 1000);
			} else {
				// throw new Error(response.message || '修改失败');
				console.error('❌ 修改密码失败:', response.message || '修改失败');
			}
		} catch (error) {
			console.error('❌ 修改密码失败:', error);
			// this.$toast.error(this.$t('account.passwordChangeFailed') || '修改失败');
		} finally {
				this.passwordLoading = false;
			}
		},
		
		// ========== 客服相关方法 ==========
		async loadCustomerServiceConfig() {
			try {
				const response = await this.$http.get('/swx/settings/customerService');
				if (response.code === 200 && response.result) {
					this.customerServiceConfig.whatsappLink = response.result.whatsappLink || 'https://wa.me/16236988384';
					this.customerServiceConfig.whatsappNumber = response.result.whatsappNumber || '+16236988384';
					console.log('✅ 客服配置已加载:', this.customerServiceConfig);
				}
			} catch (error) {
				console.log('⚠️ 客服配置加载失败，使用默认配置:', error);
			}
		},
		
		openWhatsApp() {
			// 动态加载最新配置
			this.loadCustomerServiceConfig().then(() => {
				window.open(this.customerServiceConfig.whatsappLink, '_blank');
			}).catch(() => {
				// 失败时使用默认链接
				window.open('https://wa.me/16236988384', '_blank');
			});
		},
		
	openOnlineService() {
		const origin = window.location.origin;
		window.open(origin + '/service.html?language=en', '_blank');
	},
	
	// ========== 公告通知相关方法 ==========
	async loadNoticeList() {
		try {
			this.noticeLoading = true;
			
			// 固定使用语言ID=2（英文），不管当前用户选择什么语言
			const languageId = 2;
			const res = await this.$http.get(`/swx/customer/index/listContents/${languageId}`);
			
			if (res.code === 200) {
				// 只显示标题为"通知"的记录
				const allResults = res.result || [];
				this.noticeList = allResults.filter(item => item.title === '通知');
			} else {
				console.error('❌ 获取通知失败:', res.message);
			}
		} catch (error) {
			console.error('❌ 获取通知失败:', error);
			this.$toast.error(this.$t('balanceLog.fetchError') || '获取记录失败');
		} finally {
			this.noticeLoading = false;
		}
	},
	
	// 格式化日期
	formatNoticeDate(dateStr) {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	},
	
	// 判断是否为银行地址
	isBankAddress(address) {
		if (!address) return false;
		// 检查是否包含银行信息的特征（包含 "|" 分隔符和中文字段名）
		return address.includes('|') && (address.includes('银行名称:') || address.includes('开户行地址:') || address.includes('姓名:') || address.includes('IBAN:'));
	},
	
	// 解析银行信息
	parseBankInfo(address) {
		if (!address) return [];
		
		const parts = address.split('|');
		const bankInfo = [];
		
		parts.forEach(part => {
			const [key, value] = part.split(':');
			if (key && value) {
				// 根据中文字段名获取翻译后的label
				let labelKey = '';
				switch(key) {
					case '银行名称':
						labelKey = 'yinHangMingCheng';
						break;
					case '开户行地址':
						labelKey = 'kaiHuHangDiZhi';
						break;
					case '姓名':
						labelKey = 'xingMing';
						break;
					case 'IBAN':
						labelKey = 'IBAN';
						break;
					default:
						labelKey = key;
				}
				
				const label = labelKey === 'IBAN' ? 'IBAN' : (this.$t(labelKey) || key);
				bankInfo.push({ label, value });
			}
		});
		
		return bankInfo;
	}
}
};
</script>

<style scoped>
/* 覆盖层 */
.account-center-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	transition: all 0.3s ease;
}

/* 容器 */
.account-center-container {
	width: 1200px;
	max-width: 95vw;
	height: 700px;
	max-height: 90vh;
	background: var(--card-bg, #ffffff);
	border-radius: 16px;
	box-shadow: var(--modal-shadow, 0 8px 32px rgba(0, 0, 0, 0.12));
	overflow: hidden;
	position: relative;
	transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

/* 关闭按钮 */
.close-btn {
	position: absolute;
	top: 20px;
	right: 20px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	border-radius: 50%;
	background: var(--bg-secondary, #f5f5f5);
	color: var(--text-secondary, #666);
	transition: all 0.3s ease;
	z-index: 10;
}

.close-btn:hover {
	background: var(--hover-bg, rgba(0, 0, 0, 0.1));
	color: var(--text-primary, #333);
}

.close-btn i {
	font-size: 18px;
}

/* 内容布局 */
.account-center-content {
	display: flex;
	height: 100%;
}

/* 左侧菜单 */
.account-sidebar {
	width: 200px;
	background: var(--card-bg, #ffffff);
	border-right: 1px solid var(--border-light, #e4e7ed);
	padding: 20px 0;
	transition: background-color 0.3s ease, border-color 0.3s ease;
	overflow-y: auto;
}

.menu-item {
	padding: 15px 30px;
	cursor: pointer;
	font-size: 15px;
	font-weight: 500;
	color: var(--text-primary, #333);
	transition: all 0.3s ease;
	border-left: 3px solid transparent;
	text-align: center;
}

.menu-item:hover {
	background: var(--hover-bg, rgba(1, 102, 252, 0.05));
	color: var(--primary-color, #0156DC);
}

.menu-item.menu-active {
	background: var(--primary-color, #0156DC);
	color: white;
	border-left-color: var(--primary-color, #0156DC);
	border-radius: 8px;
	margin: 0 10px;
	padding-left: 27px;
}

.menu-item.menu-danger {
	color: var(--danger-color, #f56c6c);
}

.menu-item.menu-danger:hover {
	background: var(--danger-bg, rgba(245, 108, 108, 0.1));
	color: var(--danger-color, #f23c48);
}

/* 右侧主内容区 */
.account-main-content {
	flex: 1;
	padding: 40px;
	overflow-y: auto;
	background: var(--card-bg, #ffffff);
	transition: background-color 0.3s ease;
}

/* 内容面板 */
.content-panel {
	width: 100%;
}

.panel-title {
	margin-bottom: 30px;
	color: var(--text-primary, #333);
	font-size: 20px;
	font-weight: 600;
	transition: color 0.3s ease;
}

/* 钱包面板 */
.wallet-panel {
	padding: 0 !important;
}

/* 账户卡片网格 */
.accounts-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px;
	margin-bottom: 30px;
}

.account-card {
	padding: 24px;
	border-radius: 16px;
	color: white;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
	cursor: pointer;
}

.account-card:hover {
	transform: translateY(-5px);
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

/* 普通账户 - 使用主题色深色渐变 */
.account-card-spot {
	background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
}

/* 合约账户 - 使用主题色标准渐变 */
.account-card-contract {
	background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%);
}

/* 期权账户 - 使用主题色浅色渐变 */
.account-card-option {
	background: linear-gradient(135deg, var(--primary-light) 0%, var(--primary-color) 100%);
}

.card-header {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
}

.account-icon {
	width: 44px;
	height: 44px;
	border-radius: 12px;
	background: rgba(255, 255, 255, 0.2);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
}

.account-name {
	font-size: 16px;
	font-weight: 600;
	opacity: 0.95;
}

.account-balance {
	font-size: 30px;
	font-weight: 700;
	margin-bottom: 16px;
	line-height: 1.2;
}

.account-balance::after {
	content: ' USD';
	font-size: 14px;
	font-weight: 400;
	opacity: 0.8;
	margin-left: 6px;
}

.account-details {
	padding-top: 16px;
	border-top: 1px solid rgba(255, 255, 255, 0.2);
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.account-details .detail-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 13px;
}

.account-details .detail-row .label {
	opacity: 0.8;
}

.account-details .detail-row .value {
	font-weight: 600;
}

/* 余额变动记录区域 */
.balance-log-section {
	background: transparent;
	border-radius: 12px;
	padding: 0;
}

/* 表单行 */
.form-row {
	margin-bottom: 20px;
}

.form-label {
	font-size: 14px;
	color: var(--text-primary, #333);
	font-weight: 500;
	margin-bottom: 8px;
	display: block;
	transition: color 0.3s ease;
}

.form-input,
.form-select {
	width: 100%;
	padding: 12px;
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 8px;
	font-size: 14px;
	box-sizing: border-box;
	background: var(--input-bg, #ffffff);
	color: var(--text-primary, #333);
	transition: all 0.3s ease;
}

.form-input:focus,
.form-select:focus {
	border-color: var(--primary-color, #0156DC);
	outline: none;
	box-shadow: 0 0 0 3px rgba(1, 102, 252, 0.1);
}

.form-hint {
	font-size: 12px;
	color: var(--text-tertiary, #999);
	margin-top: 5px;
	transition: color 0.3s ease;
}

.readonly-input {
	background: var(--bg-secondary, #f5f7fa);
	color: var(--text-tertiary, #999);
	cursor: not-allowed;
}

/* 地址输入组 */
.address-input-group {
	display: flex;
	gap: 10px;
	align-items: center;
}

.address-input {
	flex: 1;
	padding: 12px;
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 8px;
	background: var(--input-bg, #ffffff);
	color: var(--text-primary, #333);
	font-family: monospace;
	font-size: 13px;
	transition: all 0.3s ease;
}

.copy-btn {
	padding: 10px 20px;
	background: var(--primary-color, #0156DC);
	color: white;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 5px;
	transition: all 0.3s ease;
}

.copy-btn:hover {
	background: var(--primary-color, #0156DC);
	opacity: 0.9;
	transform: translateY(-1px);
}

/* 二维码容器 */
.qr-code-container {
	background: var(--card-bg, #ffffff);
	padding: 15px;
	border-radius: 8px;
	text-align: center;
	border: 1px solid var(--border-light, #e4e7ed);
	display: inline-block;
	transition: all 0.3s ease;
	min-width: 180px;
	min-height: 180px;
}

.qr-code-image {
	width: 150px;
	height: 150px;
	display: block;
}

.qr-loading {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	text-align: center;
}

.qr-loading .spinner {
	border: 3px solid #f3f3f3;
	border-top: 3px solid #3498db;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin: 0 auto;
}

.qr-loading p {
	margin-top: 10px;
	font-size: 12px;
	color: #909399;
}

.qr-error {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	text-align: center;
	width: 100%;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* 上传区域 */
.upload-area {
	border: 2px dashed var(--border-color, #dcdfe6);
	border-radius: 8px;
	padding: 30px;
	text-align: center;
	cursor: pointer;
	background: var(--bg-secondary, #fafafa);
	transition: all 0.3s ease;
}

.upload-area:hover {
	border-color: var(--primary-color, #0156DC);
	background: rgba(1, 102, 252, 0.05);
}

.upload-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.upload-text {
	color: var(--text-secondary, #666);
	font-size: 14px;
	margin-bottom: 4px;
	transition: color 0.3s ease;
}

.upload-hint {
	color: var(--text-tertiary, #999);
	font-size: 12px;
	transition: color 0.3s ease;
}

.upload-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
}

.preview-image {
	max-width: 100%;
	max-height: 150px;
	border-radius: 4px;
	margin-bottom: 8px;
}

.upload-success {
	color: var(--success-color, #67c23a);
	font-size: 13px;
}

/* 金额输入组 */
.amount-input-group {
	position: relative;
	display: flex;
	align-items: center;
}

.amount-input-group .form-input {
	padding-right: 70px;
}

.max-btn {
	position: absolute;
	right: 10px;
	padding: 4px 12px;
	background: var(--primary-color, #0156DC);
	color: white;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 12px;
	transition: all 0.3s ease;
}

.max-btn:hover {
	background: var(--primary-color, #0156DC);
	opacity: 0.9;
}

/* 余额信息卡片 */
.balance-info-card {
	background: var(--bg-secondary, #f5f7fa);
	padding: 20px;
	border-radius: 8px;
	margin-bottom: 20px;
	transition: background-color 0.3s ease;
}

.info-label {
	font-size: 14px;
	color: var(--text-secondary, #666);
	margin-bottom: 5px;
	transition: color 0.3s ease;
}

.info-amount {
	font-size: 24px;
	color: var(--primary-color, #0156DC);
	font-weight: bold;
	transition: color 0.3s ease;
}

/* 手续费卡片 */
.fee-info-card {
	background: var(--bg-secondary, #f5f7fa);
	padding: 15px;
	border-radius: 8px;
	margin-bottom: 20px;
	transition: background-color 0.3s ease;
}

.fee-row {
	display: flex;
	justify-content: space-between;
	margin-bottom: 8px;
}

.fee-row:last-child {
	margin-bottom: 0;
}

.fee-label {
	color: var(--text-secondary, #666);
	font-size: 14px;
	transition: color 0.3s ease;
}

.fee-value {
	color: var(--text-primary, #333);
	font-weight: 500;
	transition: color 0.3s ease;
}

.estimated-value {
	color: var(--text-primary, #333);
	font-weight: 600;
	font-size: 16px;
	transition: color 0.3s ease;
}

/* KYC表单 */
.kyc-form-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	margin-bottom: 30px;
}

.form-group {
	margin-bottom: 0;
}

.form-group-label {
	display: block;
	font-size: 14px;
	color: var(--text-primary, #333);
	margin-bottom: 8px;
	font-weight: 500;
	transition: color 0.3s ease;
}

.form-group-input {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 4px;
	font-size: 14px;
	box-sizing: border-box;
	background: var(--input-bg, #ffffff);
	color: var(--text-primary, #333);
	transition: all 0.3s ease;
}

.form-group-input:focus {
	border-color: var(--primary-color, #0156DC);
	outline: none;
}

.phone-input-group {
	display: flex;
	gap: 8px;
}

.phone-code-select {
	flex-shrink: 0;
	padding: 10px 8px 10px 10px;
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 4px;
	font-size: 14px;
	background: var(--input-bg, #ffffff);
	color: var(--text-primary, #333);
	min-width: 80px;
	max-width: 100px;
	transition: all 0.3s ease;
}

.phone-input {
	flex: 1;
	padding: 10px;
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 4px;
	font-size: 14px;
	box-sizing: border-box;
	background: var(--input-bg, #ffffff);
	color: var(--text-primary, #333);
	transition: all 0.3s ease;
}

.upload-title {
	margin: 30px 0 20px 0;
	color: var(--text-primary, #333);
	font-size: 16px;
	font-weight: 600;
	transition: color 0.3s ease;
}

.kyc-upload-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 30px;
	margin-bottom: 20px;
}

.kyc-upload-item {
	display: flex;
	flex-direction: column;
}

.upload-item-label {
	display: block;
	font-size: 14px;
	color: var(--text-primary, #333);
	margin-bottom: 10px;
	font-weight: 500;
	transition: color 0.3s ease;
}

.kyc-upload-box {
	width: 100%;
	height: 200px;
	border: 2px dashed var(--border-color, #dcdfe6);
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background: var(--bg-secondary, #fafafa);
	transition: all 0.3s ease;
	overflow: hidden;
}

.kyc-upload-box:hover {
	border-color: var(--primary-color, #0156DC);
	background: rgba(1, 102, 252, 0.05);
}

.upload-box-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.upload-box-text {
	font-size: 14px;
	color: var(--text-secondary, #666);
	margin-top: 10px;
	transition: color 0.3s ease;
}

.kyc-preview-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

/* 邀请码显示 */
.invite-code-display {
	text-align: center;
	margin-bottom: 30px;
}

.invite-code-label {
	font-size: 14px;
	color: var(--text-secondary, #666);
	margin-bottom: 15px;
	transition: color 0.3s ease;
}

.invite-code-value {
	font-size: 36px;
	color: var(--primary-color, #0156DC);
	font-weight: bold;
	letter-spacing: 4px;
	transition: color 0.3s ease;
}

.invite-qr-display {
	text-align: center;
	margin-bottom: 30px;
}

.invite-qr-image {
	width: 200px;
	height: 200px;
	border: 1px solid var(--border-light, #e4e7ed);
	border-radius: 8px;
	transition: border-color 0.3s ease;
}

.invite-link-card {
	background: var(--bg-secondary, #f5f7fa);
	padding: 15px;
	border-radius: 8px;
	margin-bottom: 20px;
	transition: background-color 0.3s ease;
}

.invite-link-label {
	font-size: 14px;
	color: var(--text-secondary, #666);
	margin-bottom: 10px;
	transition: color 0.3s ease;
}

.invite-link-value {
	word-break: break-all;
	font-size: 12px;
	color: var(--text-primary, #333);
	font-family: monospace;
	line-height: 1.6;
	transition: color 0.3s ease;
}

/* 客服卡片 */
.support-card {
	background: var(--card-bg, #ffffff);
	border: 1px solid var(--border-light, #e4e7ed);
	border-radius: 8px;
	padding: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 15px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.support-card:hover {
	border-color: var(--primary-color, #0156DC);
	box-shadow: 0 2px 12px rgba(1, 102, 252, 0.15);
}

.support-card-content {
	display: flex;
	align-items: center;
	gap: 15px;
}

.support-icon {
	width: 50px;
	height: 50px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: all 0.3s ease;
}

.whatsapp-icon {
	background: #E8F5E9;
	color: #25D366;
}

.online-icon {
	background: #E3F2FD;
	color: var(--primary-color, #0156DC);
}

.support-info {
	flex: 1;
}

.support-title {
	font-size: 16px;
	font-weight: 500;
	color: var(--text-primary, #333);
	margin-bottom: 4px;
	transition: color 0.3s ease;
}

.support-subtitle {
	font-size: 14px;
	color: var(--text-secondary, #666);
	transition: color 0.3s ease;
}

.support-arrow {
	color: var(--text-tertiary, #999);
	font-size: 20px;
	transition: color 0.3s ease;
}

/* 空状态 */
.empty-state {
	text-align: center;
	padding: 60px 20px;
	color: var(--text-tertiary, #999);
	transition: color 0.3s ease;
}

.empty-text {
	font-size: 16px;
	font-weight: 400;
}

/* 提交按钮 */
.form-actions {
	width: 100%;
	margin-top: 20px;
}

.submit-btn {
	width: 100%;
	padding: 14px;
	background: var(--primary-color, #0156DC);
	color: white;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	font-size: 16px;
	font-weight: 600;
	transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
	background: var(--primary-color, #0156DC);
	opacity: 0.9;
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(58, 105, 250, 0.3);
}

.submit-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

/* 滚动条样式 */
.account-main-content::-webkit-scrollbar,
.account-sidebar::-webkit-scrollbar {
	width: 6px;
}

.account-main-content::-webkit-scrollbar-track,
.account-sidebar::-webkit-scrollbar-track {
	background: var(--bg-secondary, #f5f5f5);
	border-radius: 3px;
}

.account-main-content::-webkit-scrollbar-thumb,
.account-sidebar::-webkit-scrollbar-thumb {
	background: var(--border-color, #dcdfe6);
	border-radius: 3px;
}

.account-main-content::-webkit-scrollbar-thumb:hover,
.account-sidebar::-webkit-scrollbar-thumb:hover {
	background: var(--text-tertiary, #999);
}

/* 入金/出金记录样式 */
.record-section {
	margin-top: 40px;
	padding-top: 30px;
	border-top: 1px solid var(--border-light);
}

.record-title {
	font-size: 18px;
	font-weight: 600;
	color: var(--text-primary);
	margin-bottom: 20px;
	transition: color 0.3s ease;
}

.record-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.record-item {
	background: var(--bg-secondary);
	border: 1px solid var(--border-light);
	border-radius: 8px;
	padding: 16px;
	transition: all 0.3s ease;
}

.record-item:hover {
	border-color: var(--primary-color, #0156DC);
	box-shadow: 0 2px 8px var(--card-shadow);
}

.record-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.record-type {
	font-size: 15px;
	font-weight: 600;
	color: var(--text-primary);
	transition: color 0.3s ease;
}

.record-status {
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
}

.record-status.success {
	background: #67c23a20;
	color: #67c23a;
}

.record-status.pending {
	background: #e6a23c20;
	color: #e6a23c;
}

.record-status.rejected {
	background: #f56c6c20;
	color: #f56c6c;
}

.record-status.unknown {
	background: #90939920;
	color: #909399;
}

.record-details {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.detail-row {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	font-size: 13px;
}

.detail-label {
	color: var(--text-tertiary);
	flex-shrink: 0;
	margin-right: 10px;
	transition: color 0.3s ease;
}

.detail-value {
	color: var(--text-primary);
	font-weight: 500;
	text-align: right;
	word-break: break-all;
	transition: color 0.3s ease;
}

.detail-value.error-text {
	color: #f56c6c;
}

.load-more-wrapper {
	margin-top: 20px;
	text-align: center;
}

.load-more-btn {
	padding: 10px 30px;
	background: var(--primary-color, #0156DC);
	color: white;
	border: none;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.load-more-btn:hover:not(:disabled) {
	background: var(--primary-color, #0156DC);
	opacity: 0.9;
}

.load-more-btn:disabled {
	background: #ccc;
	cursor: not-allowed;
}

.no-more-text {
	text-align: center;
	padding: 20px;
	color: var(--text-tertiary);
	font-size: 14px;
	transition: color 0.3s ease;
}

.empty-records {
	text-align: center;
	padding: 60px 20px;
	color: var(--text-tertiary);
}

.empty-records i {
	display: block;
	margin-bottom: 16px;
}

.empty-records p {
	font-size: 14px;
	margin: 0;
	color: var(--text-tertiary);
	transition: color 0.3s ease;
}

/* 响应式 */
@media (max-width: 768px) {
	.account-center-container {
		width: 95vw;
		height: 95vh;
	}
	
	.account-sidebar {
		width: 160px;
	}
	
	.account-main-content {
		padding: 20px;
	}
	
	.accounts-grid {
		grid-template-columns: 1fr;
		gap: 15px;
	}
	
	.account-card {
		padding: 20px;
	}
	
	.account-balance {
		font-size: 26px;
	}
	
	.kyc-form-grid,
	.kyc-upload-grid {
		grid-template-columns: 1fr;
	}
}

/* 公告通知样式 */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	min-height: 200px;
}

.loading-text {
	color: var(--text-secondary);
	font-size: 14px;
	margin-top: 10px;
	transition: color 0.3s ease;
}

.notice-list {
	display: flex;
	flex-direction: column;
	gap: 15px;
	align-items: center;
}

.notice-item {
	background: var(--bg-secondary);
	border: 1px solid var(--border-light);
	border-radius: 8px;
	padding: 20px;
	cursor: default;
	transition: all 0.3s ease;
	max-width: 800px;
	width: 100%;
}

/* 通知内容样式 - 直接显示完整内容 */
.notice-content {
	font-size: 14px;
	color: var(--text-primary);
	line-height: 1.6;
	transition: color 0.3s ease;
	text-align: center;
}

.notice-content ::v-deep img {
	max-width: 100%;
	height: auto;
	margin: 10px auto;
	display: block;
}

.notice-content ::v-deep p {
	margin: 8px 0;
	text-align: center;
}
</style>

<!-- Element UI Select 下拉菜单全局样式 -->
<style>
/* Element UI Select 输入框样式 */
.account-center-container .el-select {
	width: 100%;
}

.account-center-container .el-select .el-input__inner {
	background-color: var(--input-bg, #ffffff) !important;
	border: 1px solid var(--border-color, #dcdfe6) !important;
	color: var(--text-primary, #333) !important;
	padding: 12px;
	border-radius: 8px;
	font-size: 14px;
	transition: all 0.3s ease;
}

.account-center-container .el-select .el-input__inner:hover {
	border-color: var(--primary-color, #0156DC) !important;
}

.account-center-container .el-select .el-input__inner:focus {
	border-color: var(--primary-color, #0156DC) !important;
	outline: none;
	box-shadow: 0 0 0 3px rgba(1, 102, 252, 0.1) !important;
}

.account-center-container .el-select .el-input__suffix {
	color: var(--text-secondary, #666) !important;
	transition: color 0.3s ease;
}

.account-center-container .el-select .el-select__caret {
	color: var(--text-secondary, #666) !important;
	transition: all 0.3s ease;
}

.account-center-container .el-select .el-select__caret.is-reverse {
	transform: rotateZ(180deg);
}

.account-center-container .el-select .el-input__inner::placeholder {
	color: var(--text-tertiary, #999) !important;
	transition: color 0.3s ease;
}

/* Element UI Select 下拉菜单样式 */
.el-select-dropdown {
	background-color: var(--card-bg, #ffffff) !important;
	border: 1px solid var(--border-color, #dcdfe6) !important;
	box-shadow: var(--dropdown-shadow, 0 2px 12px 0 rgba(0, 0, 0, 0.1)) !important;
	transition: all 0.3s ease;
	z-index: 10000 !important; /* 确保下拉菜单在最上层，高于 account-center-overlay 的 9999 */
}

.el-select-dropdown__item {
	background-color: var(--card-bg, #ffffff) !important;
	color: var(--text-primary, #333) !important;
	padding: 0 20px;
	height: 34px;
	line-height: 34px;
	transition: all 0.3s ease;
}

.el-select-dropdown__item:hover {
	background-color: var(--hover-bg, rgba(1, 102, 252, 0.1)) !important;
}

.el-select-dropdown__item.selected {
	background-color: var(--active-bg, rgba(1, 102, 252, 0.15)) !important;
	color: var(--primary-color, #0156DC) !important;
	font-weight: 600;
}

.el-select-dropdown__item.hover {
	background-color: var(--hover-bg, rgba(1, 102, 252, 0.1)) !important;
}

.el-select-dropdown__empty {
	color: var(--text-tertiary, #999) !important;
}

/* 下拉箭头和 Popper 容器 */
.el-popper {
	z-index: 10000 !important; /* 确保 Popper 容器在最上层 */
}

.el-popper[x-placement^=bottom] .popper__arrow {
	border-bottom-color: var(--border-color, #dcdfe6) !important;
}

.el-popper[x-placement^=bottom] .popper__arrow::after {
	border-bottom-color: var(--card-bg, #ffffff) !important;
}

.el-popper[x-placement^=top] .popper__arrow {
	border-top-color: var(--border-color, #dcdfe6) !important;
}

.el-popper[x-placement^=top] .popper__arrow::after {
	border-top-color: var(--card-bg, #ffffff) !important;
}

/* KYC状态显示样式 */
.kyc-status-display {
	text-align: center;
	padding: 60px 20px;
}

.status-icon-wrapper {
	margin-bottom: 20px;
	display: flex;
	justify-content: center;
	align-items: center;
}

.status-title {
	font-size: 24px;
	margin-bottom: 10px;
	font-weight: 600;
	transition: color 0.3s ease;
}

.status-title.verified {
	color: #67c23a;
}

.status-title.pending {
	color: var(--primary-color, #0156DC);
}

.status-description {
	font-size: 14px;
	color: var(--text-secondary);
	line-height: 1.6;
	transition: color 0.3s ease;
}
</style>
